import React, { useState } from 'react';

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from 'components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from 'components/ui/popover';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Loader2 } from 'lucide-react';
import { getAnalysisTrend, getAnalysisRecords } from 'Explore/Services/Analysis';

// Chart components
import TrendChart from './charts/TrendChart';
import StrengthRadar from './charts/StrengthRadar';
import VolumeQualityScatter from './charts/VolumeQualityScatter';
import QualityBar from './charts/QualityBar';
import PeerRankingStrip from './charts/PeerRankingStrip';
import GrowthMomentumChart from './charts/GrowthMomentumChart';

const PALETTES = {
  default: ['#1E4889', '#A13D44', '#286A4F', '#D97736', '#6A3D7A', '#007B8C', '#8B7355', '#4A5568'],
  colorblind: ['#0072b2', '#e69f00', '#009e73', '#cc79a7', '#56b4e9', '#d55e00'],
  warm: ['#8b2621', '#f9a826', '#f15a24', '#f7d825', '#da1f26', '#9a4d25'],
  cool: ['#1a3b8d', '#2bb6f2', '#00a85f', '#7c3aed', '#1292b4', '#2563eb'],
  grayscale: ['#1e293b', '#cbd5e1', '#475569', '#f1f5f9', '#64748b', '#0f172a']
};

const TREND_METRICS = [
  { value: "total_pubs", label: "Total Publications" },
  { value: "pubs_per_faculty", label: "Publications / Faculty" },
  { value: "total_citations", label: "Total Citations" },
  { value: "citations_per_faculty", label: "Citations / Faculty" },
  { value: "citations_per_paper", label: "Citations / Paper" },
  { value: "h_index", label: "H-Index" },
  { value: "total_grant_amount", label: "Total Grant Funding ($)" },
  { value: "grant_amount_per_faculty", label: "Grant Amount/ Faculty ($)" },
  { value: "int_collab", label: "International Collaborations" }
];

const ChartColorsPopover = ({ entities, onColorChange, onPaletteSelect }) => {
  const [activePalette, setActivePalette] = useState('default');

  const handlePaletteSelect = (key) => {
    setActivePalette(key);
    onPaletteSelect(PALETTES[key]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-md shadow-sm hover:bg-slate-50 transition-colors">
          <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          Chart Colors
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-4 bg-white border-slate-200" align="end">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-slate-800 text-sm">Chart colors</h4>
          <span className="text-[10px] text-slate-400 font-medium">palette & per-entity</span>
        </div>
        
        <div className="flex flex-col gap-3 mb-4">
          {Object.entries(PALETTES).map(([key, colors]) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer group">
              <input 
                type="radio" 
                name="palette" 
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                checked={activePalette === key}
                onChange={() => handlePaletteSelect(key)}
              />
              <span className="text-xs text-slate-600 capitalize flex-1 group-hover:text-slate-900">
                {key === 'colorblind' ? 'Colorblind-safe' : key}
              </span>
              <div className="flex gap-1">
                {colors.map((c, i) => (
                  <div key={i} className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: c }} />
                ))}
              </div>
            </label>
          ))}
        </div>

        <div className="h-px bg-slate-100 w-full mb-4 border-t border-dashed border-slate-200" />
        
        <div className="flex flex-col gap-3 mb-4 max-h-[160px] overflow-y-auto pr-1">
          {entities.map(entity => (
            <div key={entity.id} className="flex items-center gap-3">
              <div className="relative w-6 h-4 rounded-[3px] border border-slate-800 overflow-hidden shrink-0 shadow-sm cursor-pointer hover:scale-105 transition-transform" style={{ backgroundColor: entity.color }}>
                <input 
                  key={entity.color}
                  type="color" 
                  defaultValue={entity.color}
                  onBlur={(e) => onColorChange(entity.id, e.target.value)}
                  className="absolute -inset-2 opacity-0 cursor-pointer"
                />
              </div>
              <span className="text-xs text-slate-700 truncate">{entity.name}</span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => handlePaletteSelect('default')}
          className="px-4 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-sm hover:bg-slate-50 transition-colors"
        >
          Reset colors
        </button>
      </PopoverContent>
    </Popover>
  );
};

const VisualViewTest = ({
  pinnedEntities = [],
  tableRows = [],
  tableRecords = [],
  isActive = false,
  isTableReady = false,
  filters,
  entityType = "institution",
  customTrendData = null,
  isPeerMode = false,
  gridMode = false,
  displayedGridCharts = ['trend', 'growth', 'radar', 'scatter', 'quality', 'ranking'],
  initialChartType = "trend",
  initialMetricType = "total_pubs",
  topN: controlledTopN,
  onTopNChange,
}) => {
  const [chartType, setChartType] = useState(initialChartType);
  const [trendMetric, setTrendMetric] = useState(initialMetricType);
  const [internalTopN, setInternalTopN] = useState(5);
  const topN = controlledTopN !== undefined ? controlledTopN : internalTopN;
  const setTopN = onTopNChange || setInternalTopN;
  const [entities, setEntities] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);

  const displayEntities = React.useMemo(() => {
    if (pinnedEntities.length > 0) return pinnedEntities;
    return tableRows.slice(0, topN);
  }, [pinnedEntities, tableRows, topN]);

  React.useEffect(() => {
    setEntities(prev => {
      return displayEntities.map((entity, index) => {
        const existing = prev.find(e => e.id === entity.id);
        return {
          ...entity,
          color: existing?.color || PALETTES.default[index % PALETTES.default.length]
        };
      });
    });
  }, [displayEntities]);

  const lastTrendFetchRef = React.useRef(null);

  React.useEffect(() => {
    if (!isActive || !isTableReady) return;
    if (displayEntities.length === 0) return;
    if (!gridMode && chartType !== "trend" && chartType !== "growth") return;

    const currentSig = JSON.stringify({
      ids: displayEntities.map(e => e.id),
      metric: trendMetric,
      filters,
      entityType
    });

    if (lastTrendFetchRef.current === currentSig && trendData.length > 0) {
      return; // already fetched
    }
    
    if (customTrendData) {
      setTrendData(customTrendData);
      lastTrendFetchRef.current = currentSig;
      return;
    }

    let isMounted = true;
    const fetchTrend = async () => {
      setIsLoadingTrend(true);
      try {
        const result = await getAnalysisTrend({
          entityType,
          entityIds: displayEntities.map(e => e.id),
          metric: trendMetric,
          yearRange: filters?.yearRange,
          subjectArea: filters?.subjectArea,
          keywordsOperator: filters?.keywordsOperator
        });
        if (isMounted) {
          const transformed = (result.years || []).map((year, i) => {
            const point = { 
              year: year.toString(),
              is_forecast: result.is_forecast ? result.is_forecast[i] : false
            };
            (result.series || []).forEach(s => {
              point[`entity_${s.entity_id}`] = s.values[i];
            });
            return point;
          });
          setTrendData(transformed);
          lastTrendFetchRef.current = currentSig;
        }
      } catch (err) {
        console.error("Failed to fetch trend data", err);
      } finally {
        if (isMounted) setIsLoadingTrend(false);
      }
    };
    fetchTrend();
    
    return () => { isMounted = false; };
  }, [isActive, isTableReady, chartType, trendMetric, displayEntities, filters, entityType, gridMode]);

  const handleColorChange = (id, newColor) => {
    setEntities(prev => prev.map(e => e.id === id ? { ...e, color: newColor } : e));
  };

  const handlePaletteSelect = (colors) => {
    setEntities(prev => prev.map((e, index) => ({
      ...e,
      color: colors[index % colors.length]
    })));
  };

  const renderChart = () => {
    if (entities.length === 0) {
      return <div className="flex h-full items-center justify-center text-slate-400">Select an entity in Table View to begin comparison.</div>;
    }
    const metricLabel = TREND_METRICS.find(m => m.value === trendMetric)?.label || 'Metric';
    
    if (gridMode) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full h-auto">
          {displayedGridCharts.includes('trend') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><TrendChart entities={entities} trendData={trendData} isLoading={isLoadingTrend} metricLabel={metricLabel} isPeerMode={isPeerMode} /></div>}
          {displayedGridCharts.includes('growth') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><GrowthMomentumChart entities={entities} trendData={trendData} isLoading={isLoadingTrend} metricLabel={metricLabel} isPeerMode={isPeerMode} /></div>}
          {displayedGridCharts.includes('radar') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><StrengthRadar entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} /></div>}
          {displayedGridCharts.includes('scatter') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><VolumeQualityScatter entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} /></div>}
          {displayedGridCharts.includes('quality') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><QualityBar entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} /></div>}
          {displayedGridCharts.includes('ranking') && <div className="h-[400px] border rounded-lg p-4 bg-white shadow-sm flex flex-col"><PeerRankingStrip entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} /></div>}
        </div>
      );
    }

    switch (chartType) {
      case "radar":   return <StrengthRadar entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} />;
      case "scatter": return <VolumeQualityScatter entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} />;
      case "trend":   return <TrendChart entities={entities} trendData={trendData} isLoading={isLoadingTrend} metricLabel={metricLabel} isPeerMode={isPeerMode} />;
      case "growth":  return <GrowthMomentumChart entities={entities} trendData={trendData} isLoading={isLoadingTrend} metricLabel={metricLabel} isPeerMode={isPeerMode} />;
      case "quality": return <QualityBar entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} />;
      case "ranking": return <PeerRankingStrip entities={entities} analysisRecords={tableRecords} isPeerMode={isPeerMode} />;
      default:        return null;
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border border-gray-200 rounded-md">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between p-4 border-b border-gray-100 bg-slate-50/50 gap-4">
        
        <div className="flex flex-wrap items-center gap-6">
          {!gridMode && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Chart</span>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="w-[260px] h-8 text-sm bg-white">
                  <SelectValue placeholder="Select chart" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-xs font-bold text-gray-500 uppercase tracking-wider pl-2">Time Dynamics</SelectLabel>
                    <SelectItem value="trend">Trend Evolution (Line)</SelectItem>
                    <SelectItem value="growth">Growth Momentum (Bar)</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-xs font-bold text-gray-500 uppercase tracking-wider pl-2 mt-2">Snapshot Overview</SelectLabel>
                    <SelectItem value="radar">Strength Profile (Radar)</SelectItem>
                    <SelectItem value="scatter">Volume vs Quality (Scatter)</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-xs font-bold text-gray-500 uppercase tracking-wider pl-2 mt-2">Deep Comparison</SelectLabel>
                    <SelectItem value="quality">Impact Quality (Grouped Bar)</SelectItem>
                    <SelectItem value="ranking">Ranking Strip (Horizontal Bar)</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {(gridMode || chartType === "trend" || chartType === "growth") && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Metric</span>
              <Select value={trendMetric} onValueChange={setTrendMetric}>
                <SelectTrigger className="w-[200px] h-8 text-sm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TREND_METRICS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {!isPeerMode && (
            <>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold uppercase ${pinnedEntities.length > 0 ? "text-slate-300" : "text-slate-500"}`}>Top N</span>
                <Input 
                  type="number" 
                  min={1} 
                  max={25} 
                  value={topN} 
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val)) setTopN(val);
                  }}
                  disabled={pinnedEntities.length > 0}
                  className="w-16 h-8 text-sm text-center bg-white disabled:bg-slate-50"
                />
              </div>
              {pinnedEntities.length > 0 ? (
                <Badge variant="secondary" className="bg-explore/10 text-explore hover:bg-explore/20 border-explore/20">
                  Showing {pinnedEntities.length} pinned {pinnedEntities.length === 1 ? 'entity' : 'entities'}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500 bg-white">
                  Showing top {displayEntities.length} {displayEntities.length === 1 ? 'entity' : 'entities'}
                </Badge>
              )}
            </>
          )}
          <ChartColorsPopover 
            entities={entities} 
            onColorChange={handleColorChange} 
            onPaletteSelect={handlePaletteSelect} 
          />
        </div>
      </div>

      {/* Chart Area */}
      <div className="flex-1 p-6 relative">
        {(!isTableReady || isLoadingTrend) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-explore" />
              <span className="font-medium text-slate-700">Loading analysis data...</span>
            </div>
          </div>
        )}
        <div className={`w-full ${gridMode ? "h-auto" : "h-[500px]"}`}>
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

export default VisualViewTest;
